/*
  Dinowax shipping progress bar — keeps the bar in step with the cart.

  The bar is fully rendered by Liquid on every page load, so it is correct with
  JavaScript disabled and correct on a redirect-style cart where each add
  reloads the page. This file only handles the drawer case, where the cart
  changes without a navigation.

  WHY A MONKEY-PATCH.
  This theme (Flex 1.1.3) dispatches no cart events at all — no CustomEvent, no
  jQuery trigger, no Shopify.onCartUpdate. There is nothing to subscribe to.
  What it does have is a single convergence point: every in-page cart mutation
  ends up calling Shopify.theme.jsAjaxCart.updateView() — add to cart, drawer
  delete, cart-page delete, and every quantity change. It is always called as a
  fully-qualified property, never as this.updateView(), so wrapping the property
  intercepts all of them.

  That wrapper only exists when the cart is set to drawer or mini-cart, so a
  jQuery ajaxSuccess listener backs it up: every cart write in this theme goes
  through jQuery $.ajax, and that also catches jQuery-based apps writing to the
  cart. Both paths are debounced into the same refresh.

  WHY WE RE-FETCH THE SECTION RATHER THAN RECOMPUTE.
  Formatting money correctly means honouring the store's money format, the
  buyer's presentment currency under Markets, and Shopify's habit of scaling
  even zero-decimal currencies by 100. Reimplementing that here would be a
  second source of truth that drifts. The Section Rendering API re-renders this
  section server-side with the current cart, so Liquid stays the only place that
  knows how to format anything. Verified working for static sections on this
  theme.
*/
(function () {
  'use strict';

  var SELECTOR = '[data-dw-shipbar]';
  var SECTION_KEY = 'dw-shipping-bar';
  var DEBOUNCE_MS = 120;

  var timer = null;
  var seq = 0;

  function current() {
    return document.querySelector(SELECTOR);
  }

  function swap(html) {
    var existing = current();
    if (!existing) return;

    var wrapper = document.createElement('div');
    wrapper.innerHTML = html;
    var fresh = wrapper.querySelector(SELECTOR);

    // An empty response is meaningful: the bar hides itself when the cart no
    // longer qualifies to show one (all-digital cart, or hide-when-empty). Drop
    // the old markup rather than leaving a stale message on screen.
    if (!fresh) {
      var host = existing.closest('.shopify-section') || existing;
      host.parentNode && host.removeChild(host);
      return;
    }

    // Swap the inner content rather than the whole node so the browser keeps the
    // element identity and the fill's width transition animates from where it
    // was instead of snapping.
    var oldInner = existing.querySelector('.dw-shipbar__inner');
    var newInner = fresh.querySelector('.dw-shipbar__inner');
    if (oldInner && newInner) {
      oldInner.innerHTML = newInner.innerHTML;
      existing.className = fresh.className;
    } else {
      existing.innerHTML = fresh.innerHTML;
    }
  }

  function refresh() {
    if (!current()) return;

    clearTimeout(timer);
    timer = setTimeout(function () {
      var mine = ++seq;

      // Same path, so any template-specific context is preserved.
      var url = window.location.pathname + '?sections=' + encodeURIComponent(SECTION_KEY);

      fetch(url, { credentials: 'same-origin', cache: 'no-store' })
        .then(function (r) {
          if (!r.ok) throw new Error('shipping bar: HTTP ' + r.status);
          return r.json();
        })
        .then(function (data) {
          // A newer refresh already landed — this one is stale, discard it.
          if (mine !== seq) return;
          if (typeof data[SECTION_KEY] === 'string') swap(data[SECTION_KEY]);
        })
        .catch(function () {
          // Leave the server-rendered bar in place. A stale-by-one-action bar is
          // better than a blank one, and the next page load corrects it.
        });
    }, DEBOUNCE_MS);
  }

  function patchAjaxCart() {
    var w = window;
    if (!w.Shopify || !w.Shopify.theme || !w.Shopify.theme.jsAjaxCart) return false;

    var target = w.Shopify.theme.jsAjaxCart;
    if (target.__dwShipBarPatched) return true;

    var original = target.updateView;
    if (typeof original !== 'function') return false;

    target.updateView = function () {
      var result = original.apply(this, arguments);
      refresh();
      return result;
    };
    target.__dwShipBarPatched = true;
    return true;
  }

  function init() {
    // The ajax cart script is loaded blocking in the head, but the theme builds
    // Shopify.theme.jsAjaxCart on DOM ready, so it may not exist yet. Try now,
    // then a few times, then give up and rely on the ajaxSuccess fallback.
    if (!patchAjaxCart()) {
      var tries = 0;
      var poll = setInterval(function () {
        if (patchAjaxCart() || ++tries > 20) clearInterval(poll);
      }, 250);
    }

    // Network-level backstop. Covers the redirect-cart setting, where the
    // wrapper above never exists, and any app writing to the cart via jQuery.
    if (window.jQuery) {
      window.jQuery(document).ajaxSuccess(function (event, xhr, settings) {
        var url = (settings && settings.url) || '';
        if (url.indexOf('/cart/') !== -1) refresh();
      });
    }

    // Anything using window.fetch to write to the cart — Shopify's own newer
    // patterns and most modern apps — bypasses jQuery entirely.
    if (window.fetch && !window.__dwShipBarFetchPatched) {
      var nativeFetch = window.fetch;
      window.fetch = function (input) {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        var promise = nativeFetch.apply(this, arguments);
        if (url.indexOf('/cart/') !== -1 && url.indexOf('?sections=') === -1) {
          promise.then(function () { refresh(); }).catch(function () {});
        }
        return promise;
      };
      window.__dwShipBarFetchPatched = true;
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor re-renders the section on every settings change.
  document.addEventListener('shopify:section:load', function () { patchAjaxCart(); });

  // Back/forward cache can restore a page with a cart that changed in another
  // tab or at checkout.
  window.addEventListener('pageshow', function (e) { if (e.persisted) refresh(); });
})();
